import React from 'react';

// utils
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG, SCHEMA_TYPE_BOOLEAN } from '../../util/types';
import { convertCategoriesToSelectTreeOptions, constructQueryParamName } from '../../util/search';

// component imports
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import IntegerRangeFilter from './IntegerRangeFilter/IntegerRangeFilter';
import SeatsFilter from './SeatsFilter/SeatsFilter';
import BooleanFilter from './BooleanFilter/BooleanFilter';
import SingleSelectFilter from './SingleSelectFilter/SingleSelectFilter';
import MultiSelectWithSearch from './MultiSelectWithSearch/MultiSelectWithSearch';
import SelectMultipleFilterTree from './SelectMultipleFilterTree/SelectMultipleFilterTree';
import RangeSelectFilter from './RangeSelectFilter/RangeSelectFilter';
import ScalePointFilter from './ScalePointFilter/ScalePointFilter';

import {
  empleadosTramos,
  antiguedadTramos,
  facturacionTramoBounds,
  formatFacturacionLimitK,
} from '../../config/configFilters';
import { MANDATO_RANGE_GROUPS } from '../../util/mandatoRanges';
import { PROVINCIA_TODA_ESPANA } from '../../config/configListing';

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    marketplaceCurrency,
    intl,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const name = key.replace(/\s+/g, '-');
  const getAriaLabel = (label, values) => {
    const status = values ? 'active' : 'inactive';
    const mode = liveEdit ? 'live' : 'normal';
    return intl.formatMessage(
      { id: 'SearchPage.screenreader.openFilterButton' },
      { label, status, values, mode }
    );
  };

  // Default filters: price, keywords, dates
  switch (schemaType) {
    case 'category': {
      const { scope, isNestedEnum, nestedParams } = config;
      const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope));
      const label = intl.formatMessage({ id: 'FilterComponent.categoryLabel' });

      return (
        <SelectSingleFilter
          name={key}
          label={label}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={convertCategoriesToSelectTreeOptions(listingCategories)}
          isNestedEnum={isNestedEnum}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'listingType': {
      const { scope, options } = config;
      const paramNames = [constructQueryParamName(key, scope)];
      const label = intl.formatMessage({ id: 'FilterComponent.listingTypeLabel' });

      return (
        <SelectSingleFilter
          name={key}
          label={label}
          queryParamNames={[paramNames]}
          initialValues={initialValues(paramNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={options}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'keywords':
      const label = intl.formatMessage({ id: 'FilterComponent.keywordsLabel' });

      return (
        <KeywordFilter
          label={label}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    case 'seats': {
      const label = intl.formatMessage({ id: 'FilterComponent.seatsLabel' });
      return (
        <SeatsFilter
          name={name}
          label={label}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, filterType, searchable, searchPlaceholder } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      // FASE2-SCHEMA-01.4: tipo_operacion pasó a multi-enum (el vendedor puede
      // ofrecer varias operaciones), así que ya no entra por aquí: lo atiende
      // el case SCHEMA_TYPE_MULTI_ENUM con SelectMultipleFilter, igual que
      // `tiposOperacion` del mandato. El SingleSelectFilter que había aquí se
      // retira para no dejar una rama muerta que parezca viva.
      //
      // SECTOR-FILTER-01: hierarchical multi-select tree (11 madres
      // + 55 subsectores) with tri-state parents and inline search.
      //
      // FASE2-BUSCADOR-3d: este bloque se restaura. Al retirar el de
      // tipo_operacion —estaba pegado justo encima— se fue también este, y
      // `sector` cayó al caso genérico de enum, que solo conoce el primer
      // nivel: el sidebar pasó a pintar 11 casillas planas y a mandar el slug
      // de la madre tal cual. Ningún anuncio guarda ese slug —guardan la
      // hoja—, así que el filtro devolvía 0 resultados siempre. El árbol ya
      // expande la madre a sus hojas al marcarla (toggleParent), que es lo que
      // hace que la consulta encuentre algo. Hay un test que lo fija.
      if (key === 'sector') {
        return (
          <SelectMultipleFilterTree
            label={label}
            options={enumOptions}
            queryParamNames={queryParamNames}
            initialValues={initialValues(queryParamNames, liveEdit)}
            onSubmit={getHandleChangedValueFn(useHistoryPush)}
            searchable={true}
            searchPlaceholder={searchPlaceholder || 'Buscar sector…'}
          />
        );
      }
      // FACTURACION-IDX-01 (3b): el filtro de facturación dejó de ser el
      // dropdown multi-tramo (pub_facturacion, showFilter: false) y pasa
      // a ser el Desde–Hasta sobre pub_facturacionIdx — ver el branch de
      // 'facturacionIdx' en SCHEMA_TYPE_LONG más abajo.
      // FILTERS-04: provincia uses the MultiSelectWithSearch combobox
      // (52 options, autocomplete + multi-checkbox).
      if (key === 'provincia') {
        const mappedOptions = (enumOptions || []).map(o => ({
          value: o.option,
          label: o.label,
        }));
        return (
          <MultiSelectWithSearch
            label={label}
            placeholder="Provincia"
            searchPlaceholder="Buscar provincia..."
            options={mappedOptions}
            queryParamNames={queryParamNames}
            initialValues={initialValues(queryParamNames, liveEdit)}
            onSubmit={getHandleChangedValueFn(useHistoryPush)}
          />
        );
      }
      return filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          label={label}
          getAriaLabel={getAriaLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          isNestedEnum={false}
          searchable={!!searchable}
          searchPlaceholder={searchPlaceholder}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          label={label}
          getAriaLabel={getAriaLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={enumOptions}
          schemaType={schemaType}
          searchable={!!searchable}
          searchPlaceholder={searchPlaceholder}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      // FASE2-BUSCADOR-3d: en `provincias` el vendedor marca DÓNDE ESTÁ su
      // negocio, y un mandato de ámbito nacional también le encaja. El comodín
      // se añade al emitir y se retira al leer, así que ni aparece en la lista
      // —"Toda España" no es un sitio donde estar— ni ensucia el estado del
      // sidebar: la casilla que se ve marcada es la provincia que eligió.
      const paramName = queryParamNames[0];
      const onSubmitBase = getHandleChangedValueFn(useHistoryPush);

      // FASE2-BUSCADOR-3d: en `provincias` el vendedor marca DÓNDE ESTÁ su
      // negocio, y un mandato de ámbito nacional también le encaja, así que el
      // comodín se añade al emitir y se retira al leer. Ni aparece en la lista
      // —"Toda España" no es un sitio donde estar— ni ensucia el sidebar: la
      // casilla que se ve marcada es la provincia que eligió.
      //
      // Mismo combobox que `provincia` (el campo del anuncio de venta): son 52
      // opciones y el filterConfig ya pedía buscador. Con la lista de casillas
      // a pelo, el sidebar medía más de 4000px.
      if (key === 'provincias') {
        const sinComodin = raw =>
          `${raw}`
            .replace(/^has_(any|all):/, '')
            .split(',')
            .filter(v => v && v !== PROVINCIA_TODA_ESPANA);

        const iniciales = initialValues(queryParamNames, liveEdit);
        const visibles = iniciales?.[paramName]
          ? { [paramName]: sinComodin(iniciales[paramName]).join(',') }
          : iniciales;

        return (
          <MultiSelectWithSearch
            label={label}
            placeholder="Provincia"
            searchPlaceholder="Buscar provincia..."
            options={(enumOptions || [])
              .filter(o => o.option !== PROVINCIA_TODA_ESPANA)
              .map(o => ({ value: o.option, label: o.label }))}
            queryParamNames={queryParamNames}
            initialValues={visibles}
            onSubmit={valores => {
              const elegido = valores?.[paramName];
              const lista = elegido ? sinComodin(elegido) : [];
              return onSubmitBase({
                [paramName]:
                  lista.length > 0
                    ? `${searchMode ? `${searchMode}:` : ''}${[
                        ...lista,
                        PROVINCIA_TODA_ESPANA,
                      ].join(',')}`
                    : null,
              });
            }}
          />
        );
      }

      return (
        <SelectMultipleFilter
          label={label}
          getAriaLabel={getAriaLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={onSubmitBase}
          options={enumOptions}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_BOOLEAN: {
      const { scope, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <BooleanFilter
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_LONG: {
      const { minimum, maximum, scope, step: topStep, filterConfig = {} } = config;
      const { label, step: filterStep } = filterConfig;
      const step = filterStep || topStep;
      const queryParamNames = [constructQueryParamName(key, scope)];

      // FASE2-BUSCADOR-3d: las cuatro dimensiones de escala del mandato. En
      // /compradores quien filtra es un VENDEDOR: mete SU dato —lo que
      // factura, cuánta plantilla tiene— y busca mandatos cuyo intervalo lo
      // contenga. Un solo control escribe los DOS extremos, por eso no sirve
      // ningún filtro del template. Se engancha a la clave Min; la Max sigue
      // indexada pero con showFilter:false, para no pintar dos filtros por
      // dimensión.
      const grupoDeEscala = MANDATO_RANGE_GROUPS.find(g => g.minKey === key);
      if (grupoDeEscala) {
        return (
          <ScalePointFilter
            label={label}
            scale={grupoDeEscala.scale}
            queryParamNames={[grupoDeEscala.minName, grupoDeEscala.maxName]}
            initialValues={initialValues([grupoDeEscala.minName, grupoDeEscala.maxName], liveEdit)}
            onSubmit={getHandleChangedValueFn(useHistoryPush)}
          />
        );
      }

      // RANGE-SELECT-01 (3b): facturación y empleados usan el filtro
      // Desde–Hasta (dos selects) emitiendo un rango long "min,max".
      //
      // facturacionIdx: índice ordinal 1..10 del tramo (campo calculado
      // en el wizard). "Desde" lista los límites inferiores de tramo y
      // "Hasta" los superiores (el tramo abierto "Más de 25 M€" se
      // incluye dejando "Hasta" vacío).
      if (key === 'facturacionIdx') {
        const bounds = facturacionTramoBounds();
        const fromOptions = bounds.map(b => ({
          value: b.idx,
          label: formatFacturacionLimitK(b.lowerK),
        }));
        const toOptions = bounds
          .filter(b => b.upperK != null)
          .map(b => ({ value: b.idx, label: formatFacturacionLimitK(b.upperK) }));
        return (
          <RangeSelectFilter
            label={label}
            fromOptions={fromOptions}
            toOptions={toOptions}
            queryParamNames={queryParamNames}
            initialValues={initialValues(queryParamNames, liveEdit)}
            onSubmit={getHandleChangedValueFn(useHistoryPush)}
          />
        );
      }
      // FASE2-SCHEMA-01.2: empleadosIdx — índice ordinal 1..5 del tramo
      // de plantilla (campo calculado en el wizard, igual que
      // facturacionIdx). El rango se emite sobre las POSICIONES de
      // EMPLEADOS_SCALE, no sobre el nº de empleados, para que el
      // matching con el mandato compare la misma escala. Ambos selects
      // listan los tramos con su label visible.
      if (key === 'empleadosIdx') {
        const opts = empleadosTramos().map(t => ({
          value: t.idx,
          label: t.label,
        }));
        return (
          <RangeSelectFilter
            label={label}
            fromOptions={opts}
            toOptions={opts}
            queryParamNames={queryParamNames}
            initialValues={initialValues(queryParamNames, liveEdit)}
            onSubmit={getHandleChangedValueFn(useHistoryPush)}
          />
        );
      }
      // FASE2-SCHEMA-01.3: antiguedadIdx — índice ordinal 1..5 del tramo
      // de antigüedad (campo calculado en el wizard). Sustituye al
      // SelectMultipleFilter sobre el enum `antiguedad`: el intervalo del
      // mandato es un rango, y un multi-select de slugs no se cruza con
      // un rango. Ambos selects listan los tramos con su label visible,
      // igual que empleadosIdx.
      if (key === 'antiguedadIdx') {
        const opts = antiguedadTramos().map(t => ({
          value: t.idx,
          label: t.label,
        }));
        return (
          <RangeSelectFilter
            label={label}
            fromOptions={opts}
            toOptions={opts}
            queryParamNames={queryParamNames}
            initialValues={initialValues(queryParamNames, liveEdit)}
            onSubmit={getHandleChangedValueFn(useHistoryPush)}
          />
        );
      }
      return (
        <IntegerRangeFilter
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={minimum}
          max={maximum}
          step={step}
          getAriaLabel={getAriaLabel}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
